import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Package, DollarSign, Camera, History, Clock } from 'lucide-react';
import { db } from '../firebase/config';
import { collection, addDoc, query, where, getDocs, limit, doc, setDoc, getDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { addDays } from 'date-fns';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { PhotoUpload } from '../components/PhotoUpload';
import { lookupProduct, ProductInfo, NutritionData } from '../services/productLookup';
import { useSubscriptionLimits } from '../hooks/useSubscription';
import { UpgradePrompt } from '../components/UpgradePrompt';
import { EMOJIS } from '../constants/emojis';

interface ScanHistoryItem {
  barcode: string;
  name: string;
  image?: string;
  brand?: string;
  category?: 'fridge' | 'freezer' | 'pantry';
  expirationDays?: number;
  allergens?: string[];
  nutrition?: NutritionData;
  lastScanned: Date;
  scanCount: number;
}

export function AddItem() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [category, setCategory] = useState<string>('fridge');
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [expirationDate, setExpirationDate] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // Prevent multiple scans
  const [scannedProduct, setScannedProduct] = useState<{
    image?: string;
    allergens?: string[];
    brand?: string;
    nutrition?: NutritionData;
  } | null>(null);
  const [scanHistory, setScanHistory] = useState<ScanHistoryItem[]>([]);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [limitType, setLimitType] = useState<'items' | 'members' | 'scans'>('items'); // âœ… ADDED
  const [uploadedPhotoURL, setUploadedPhotoURL] = useState<string>('');
  const [householdId, setHouseholdId] = useState<string>('');
  const subscription = useSubscriptionLimits();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!user) throw new Error('Not authenticated');

      // Get user's household
      const householdsQuery = query(
        collection(db, 'households'),
        where('members', 'array-contains', user.uid),
        limit(1)
      );

      const householdsSnapshot = await getDocs(householdsQuery);

      if (householdsSnapshot.empty) {
        throw new Error('No household found');
      }

      const householdId = householdsSnapshot.docs[0].id;

      // Check subscription limits
      if (!subscription.canAddItem) {
        setLoading(false);
        setLimitType('items'); // âœ… ADDED
        setShowUpgradePrompt(true);
        return;
      }

      // Add item to Firestore
      await addDoc(collection(db, 'items'), {
        householdId,
        name,
        category,
        expirationDate: new Date(expirationDate).toISOString(),
        quantity,
        price: price ? parseFloat(price) : 0,
        status: 'active',
        addedBy: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        // Include nutrition and allergen data if available from barcode scan
        ...(scannedProduct?.allergens && { allergens: scannedProduct.allergens }),
        ...(scannedProduct?.nutrition && { nutrition: scannedProduct.nutrition }),
        ...(scannedProduct?.brand && { brand: scannedProduct.brand }),
        // Include photo URL - prefer user-uploaded photo over product image from barcode
        ...((uploadedPhotoURL || scannedProduct?.image) && {
          photoURL: uploadedPhotoURL || scannedProduct?.image
        }),
      });

      // Success! Go back to dashboard
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  }

  // âœ… NEW FUNCTION: Check scan limit before opening scanner
  function handleScanButtonClick() {
    // Check if user has scans remaining
    if (!subscription.canScanBarcode) {
      setLimitType('scans');
      setShowUpgradePrompt(true);
      return;
    }
    
    // Open scanner
    setShowScanner(true);
  }

  async function handleBarcodeScanned(barcode: string) {
    // Prevent multiple scans
    if (isProcessing) {
      console.log('Already processing, ignoring');
      return;
    }

    console.log('Barcode scanned:', barcode);
    
    setIsProcessing(true);
    setShowScanner(false);
    setLookingUp(true);
    setError('');

    try {
      // Simple: just look up the product
      const productInfo = await lookupProduct(barcode);

      // Done looking up
      setLookingUp(false);

      if (productInfo) {
        // Product found - auto-fill form
        console.log('Product found:', productInfo.name);
        setName(productInfo.name);
        
        if (productInfo.category) {
          setCategory(productInfo.category);
        }

        if (productInfo.expirationDays) {
          const expDate = addDays(new Date(), productInfo.expirationDays);
          setExpirationDate(expDate.toISOString().split('T')[0]);
        }
        
        // Store product info for display
        setScannedProduct({
          image: productInfo.image,
          allergens: productInfo.allergens,
          brand: productInfo.brand,
          nutrition: productInfo.nutrition,
        });

        // âœ… ADDED: Increment scan count for free/trial users
        await subscription.incrementScanCount();

        // Save to scan history
        saveScanHistory(barcode, productInfo);
      } else {
        // Product not found
        console.log('Product not found');
        setError(`Product not found for barcode: ${barcode}. Please enter details manually.`);
        setScannedProduct(null);
      }
    } catch (err: any) {
      console.error('Error:', err);
      setLookingUp(false);
      setError(`Error looking up product. Please enter details manually.`);
      setScannedProduct(null);
    } finally {
      setIsProcessing(false);
    }
  }

  function setQuickDate(days: number) {
    const date = addDays(new Date(), days);
    setExpirationDate(date.toISOString().split('T')[0]);
  }

  // Save product to scan history
  async function saveScanHistory(barcode: string, productInfo: ProductInfo) {
    if (!user) return;

    try {
      const historyRef = doc(db, 'scanHistory', user.uid, 'items', barcode);
      
      // Check if item already exists to increment count
      const existingDoc = await getDocs(
        query(collection(db, 'scanHistory', user.uid, 'items'), where('__name__', '==', barcode), limit(1))
      );

      const scanCount = existingDoc.empty ? 1 : (existingDoc.docs[0].data().scanCount || 0) + 1;

      await setDoc(historyRef, {
        barcode,
        name: productInfo.name,
        image: productInfo.image || null,
        brand: productInfo.brand || null,
        category: productInfo.category || 'fridge',
        expirationDays: productInfo.expirationDays || 7,
        allergens: productInfo.allergens || [],
        nutrition: productInfo.nutrition || null,
        lastScanned: serverTimestamp(),
        scanCount,
      });

      // Reload history to show updated list
      loadScanHistory();
    } catch (err) {
      console.error('Error saving scan history:', err);
    }
  }

  // Load scan history from Firestore
  async function loadScanHistory() {
    if (!user) return;

    try {
      const historyQuery = query(
        collection(db, 'scanHistory', user.uid, 'items'),
        orderBy('lastScanned', 'desc'),
        limit(10)
      );

      const snapshot = await getDocs(historyQuery);
      const history: ScanHistoryItem[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          barcode: data.barcode,
          name: data.name,
          image: data.image,
          brand: data.brand,
          category: data.category,
          expirationDays: data.expirationDays,
          allergens: data.allergens,
          nutrition: data.nutrition,
          lastScanned: data.lastScanned?.toDate() || new Date(),
          scanCount: data.scanCount || 1,
        };
      });

      setScanHistory(history);
    } catch (err) {
      console.error('Error loading scan history:', err);
    }
  }

  // Auto-fill from scan history
  function fillFromHistory(item: ScanHistoryItem) {
    setName(item.name);
    if (item.category) setCategory(item.category);
    if (item.expirationDays) {
      const expDate = addDays(new Date(), item.expirationDays);
      setExpirationDate(expDate.toISOString().split('T')[0]);
    }
    if (item.image || item.allergens || item.brand || item.nutrition) {
      setScannedProduct({
        image: item.image,
        allergens: item.allergens,
        brand: item.brand,
        nutrition: item.nutrition,
      });
    }
  }

  useEffect(() => {
    loadScanHistory();
  }, [user]);

  // Load household ID and custom categories
  useEffect(() => {
    async function loadHouseholdData() {
      if (!user) return;

      try {
        const householdsQuery = query(
          collection(db, 'households'),
          where('members', 'array-contains', user.uid),
          limit(1)
        );
        const householdsSnapshot = await getDocs(householdsQuery);
        if (!householdsSnapshot.empty) {
          const householdDoc = householdsSnapshot.docs[0];
          const actualHouseholdId = householdDoc.id;
          setHouseholdId(actualHouseholdId);

          console.log('Actual household ID:', actualHouseholdId);

          // Load custom categories
          const householdData = householdDoc.data();
          setCustomCategories(householdData.customCategories || []);

          // Fix user's householdId if it's wrong
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('User document householdId:', userData.householdId);

            if (userData.householdId !== actualHouseholdId) {
              console.warn('User householdId mismatch! Fixing...');
              await setDoc(userDocRef, { householdId: actualHouseholdId }, { merge: true });
              console.log('User householdId updated to:', actualHouseholdId);
            }
          }
        }
      } catch (err) {
        console.error('Error loading household:', err);
      }
    }

    loadHouseholdData();
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-neutral-bg flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Please log in to add items</p>
          <Link to="/login" className="text-primary-green hover:underline">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-bg pb-20">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-primary-green transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Add Item</h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {/* Scan Button */}
          <button
            onClick={handleScanButtonClick}
            className="w-full mb-6 bg-gradient-to-r from-primary-green to-emerald-600 text-white py-4 rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-105 flex items-center justify-center gap-2"
          >
            <Camera className="w-6 h-6" />
            Scan Barcode
          </button>

          {/* Scanned Product Info */}
          {scannedProduct && (
            <div className="mb-6 bg-light-mint p-4 rounded-xl border border-primary-green/20">
              <div className="flex gap-4">
                {scannedProduct.image && (
                  <img
                    src={scannedProduct.image}
                    alt="Product"
                    className="w-20 h-20 object-contain rounded-lg bg-white"
                  />
                )}
                <div className="flex-1">
                  {scannedProduct.brand && (
                    <p className="text-sm text-primary-green font-semibold">{scannedProduct.brand}</p>
                  )}
                  {scannedProduct.allergens && scannedProduct.allergens.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-600 font-semibold">Allergens:</p>
                      <p className="text-xs text-gray-600">{scannedProduct.allergens.join(', ')}</p>
                    </div>
                  )}
                  {scannedProduct.nutrition && (
                    <div className="mt-2 pt-2 border-t border-primary-green/20">
                      <p className="text-xs text-gray-600 font-semibold mb-1">Nutrition (per 100g):</p>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600">
                        {scannedProduct.nutrition.calories !== undefined && (
                          <span><strong>Calories:</strong> {scannedProduct.nutrition.calories} kcal</span>
                        )}
                        {scannedProduct.nutrition.protein !== undefined && (
                          <span><strong>Protein:</strong> {scannedProduct.nutrition.protein}g</span>
                        )}
                        {scannedProduct.nutrition.carbohydrates !== undefined && (
                          <span><strong>Carbs:</strong> {scannedProduct.nutrition.carbohydrates}g</span>
                        )}
                        {scannedProduct.nutrition.fat !== undefined && (
                          <span><strong>Fat:</strong> {scannedProduct.nutrition.fat}g</span>
                        )}
                        {scannedProduct.nutrition.fiber !== undefined && (
                          <span><strong>Fiber:</strong> {scannedProduct.nutrition.fiber}g</span>
                        )}
                        {scannedProduct.nutrition.sodium !== undefined && (
                          <span><strong>Sodium:</strong> {scannedProduct.nutrition.sodium}mg</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Scan History */}
          {scanHistory.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-5 h-5 text-gray-600" />
                <h3 className="text-sm font-semibold text-gray-700">Recent Scans</h3>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {scanHistory.map((item) => (
                  <button
                    key={item.barcode}
                    onClick={() => fillFromHistory(item)}
                    className="flex-shrink-0 group bg-white border-2 border-gray-200 hover:border-primary-green rounded-xl p-3 transition-all w-32"
                  >
                    {item.image && (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-16 h-16 object-contain mx-auto mb-2 rounded-lg"
                      />
                    )}
                    <div className="text-left">
                      <p className="font-medium text-sm text-gray-900 truncate group-hover:text-primary-green">
                        {item.name}
                      </p>
                      {item.brand && (
                        <p className="text-xs text-gray-500 truncate">{item.brand}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500">
                          Scanned {item.scanCount}x
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Fixed height container - ALWAYS visible to prevent ANY layout shift */}
          <div className="mb-6 h-[68px] flex items-center">
            {error ? (
              <div className="w-full bg-red-50 text-red-800 p-4 rounded-xl border border-red-200">
                {error}
              </div>
            ) : lookingUp ? (
              <div className="w-full bg-light-mint text-primary-green p-4 rounded-xl border border-primary-green/20 flex items-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-green border-t-transparent"></div>
                <span className="font-medium">Looking up product...</span>
              </div>
            ) : (
              <div className="w-full h-full"></div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Product Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                What did you buy?
              </label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Chicken Breast, Milk, Yogurt"
                  required
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Where is it stored?
              </label>
              {/* Standard Categories */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3">
                <button
                  type="button"
                  onClick={() => setCategory('fridge')}
                  className={`p-3 sm:p-4 rounded-xl border-2 transition-all touch-manipulation ${
                    category === 'fridge'
                      ? 'border-primary-green bg-primary-mint/30'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">{EMOJIS.FRIDGE}</div>
                  <div className="text-xs sm:text-sm font-semibold">Fridge</div>
                </button>
                <button
                  type="button"
                  onClick={() => setCategory('freezer')}
                  className={`p-3 sm:p-4 rounded-xl border-2 transition-all touch-manipulation ${
                    category === 'freezer'
                      ? 'border-primary-green bg-primary-mint/30'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">{EMOJIS.FREEZER}</div>
                  <div className="text-xs sm:text-sm font-semibold">Freezer</div>
                </button>
                <button
                  type="button"
                  onClick={() => setCategory('pantry')}
                  className={`p-3 sm:p-4 rounded-xl border-2 transition-all touch-manipulation ${
                    category === 'pantry'
                      ? 'border-primary-green bg-primary-mint/30'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-2xl sm:text-3xl mb-1 sm:mb-2">{EMOJIS.PANTRY}</div>
                  <div className="text-xs sm:text-sm font-semibold">Pantry</div>
                </button>
              </div>

              {/* Custom Categories */}
              {customCategories.length > 0 && (
                <div>
                  <p className="text-xs text-gray-600 mb-2 font-medium">Custom Categories:</p>
                  <div className="flex flex-wrap gap-2">
                    {customCategories.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setCategory(cat)}
                        className={`px-4 py-2 rounded-lg border-2 transition-all text-sm font-medium ${
                          category === cat
                            ? 'border-primary-green bg-primary-mint/30 text-primary-green'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Price and Quantity - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  How much did it cost?
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Optional - helps track savings</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  How many?
                </label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value))}
                  min={1}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                />
              </div>
            </div>

            {/* Expiration Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                When does it expire?
              </label>

              {/* Quick Date Buttons */}
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setQuickDate(3)}
                  className="px-3 sm:px-4 py-2.5 sm:py-2 border-2 border-gray-200 rounded-lg hover:border-primary-green hover:bg-primary-mint/20 transition-all text-xs sm:text-sm font-medium touch-manipulation"
                >
                  3 days
                </button>
                <button
                  type="button"
                  onClick={() => setQuickDate(5)}
                  className="px-3 sm:px-4 py-2.5 sm:py-2 border-2 border-gray-200 rounded-lg hover:border-primary-green hover:bg-primary-mint/20 transition-all text-xs sm:text-sm font-medium touch-manipulation"
                >
                  5 days
                </button>
                <button
                  type="button"
                  onClick={() => setQuickDate(7)}
                  className="px-3 sm:px-4 py-2.5 sm:py-2 border-2 border-gray-200 rounded-lg hover:border-primary-green hover:bg-primary-mint/20 transition-all text-xs sm:text-sm font-medium touch-manipulation"
                >
                  1 week
                </button>
                <button
                  type="button"
                  onClick={() => setQuickDate(14)}
                  className="px-3 sm:px-4 py-2.5 sm:py-2 border-2 border-gray-200 rounded-lg hover:border-primary-green hover:bg-primary-mint/20 transition-all text-xs sm:text-sm font-medium touch-manipulation"
                >
                  2 weeks
                </button>
              </div>

              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all"
                />
              </div>
            </div>

            {/* Photo Upload */}
            {householdId && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Add a photo (optional)
                </label>
                <PhotoUpload
                  householdId={householdId}
                  onPhotoUploaded={(url) => setUploadedPhotoURL(url)}
                  onPhotoRemoved={() => setUploadedPhotoURL('')}
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-green text-white py-4 rounded-xl font-semibold hover:bg-primary-green/90 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-green/30 transition-all hover:scale-105"
            >
              {loading ? 'Adding...' : 'Add to Fridge'}
            </button>
          </form>
        </div>
      </div>

      {/* Barcode Scanner Modal */}
      {showScanner && (
        <BarcodeScanner
          onScanSuccess={handleBarcodeScanned}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        limitType={limitType}
        currentCount={
          limitType === 'items' ? subscription.currentItemCount :
          limitType === 'scans' ? subscription.currentScanCount :
          subscription.currentMemberCount
        }
        maxCount={
          limitType === 'items' ? subscription.maxItems :
          limitType === 'scans' ? subscription.maxBarcodeScans :
          subscription.maxMembers
        }
      />
    </div>
  );
}
